import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid, makeStyles } from "@material-ui/core";
import dayjs from "dayjs";
import * as React from "react";
import { useForm, Controller } from "react-hook-form";

import { MonthlyCashflowResult } from "@/api/generated";
import { CustomButton } from "@/components/Button";
import { PropertyEditErrorSummary } from "@/components/property_edit/PropertyEditErrorSummary";
import { CustomFormLabel } from "@/components/property_edit/PropertyEditInputs";
import { CustomForm } from "@/components/property_edit/PropertyEditInputs";
import theme from "@/theme";
import { parseFormattedNumber } from "@/utilities/format";
import { createManagementCostHistorySchema, ManagementCostHistoryFormData } from "@/validations/management/edit";

import { useEditMonthlyCashflowResult } from "../../../hooks/useEditMonthlyCashflowResult";

import { getManagementCostTypeKey, type ManagementCostType } from "./types";

const useStyles = makeStyles(() => ({
  background: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    padding: 16,
  },
}));

// 履歴編集用コンポーネント
export const ManagementCostHistoryEditor: React.FC<{
  monthlyCashflowResult?: MonthlyCashflowResult;
  costType: ManagementCostType;
  onCancel: () => void;
  onSave?: () => void;
  onCreate?: () => void;
  propertyBoughtAt: string;
}> = ({ monthlyCashflowResult, costType, onCancel, onSave, onCreate, propertyBoughtAt }) => {
  const classes = useStyles();
  const dynamicSchema = createManagementCostHistorySchema(propertyBoughtAt);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<ManagementCostHistoryFormData>({
    resolver: zodResolver(dynamicSchema),
    defaultValues: {
      month: dayjs(monthlyCashflowResult?.month).format("YYYY-MM") || "",
      building_management_cost: monthlyCashflowResult?.building_management_cost?.toString() ?? "",
      management_cost: monthlyCashflowResult?.management_cost?.toString() ?? "",
      repair_fund_cost: monthlyCashflowResult?.repair_fund_cost?.toString() ?? "",
      communication_cost: monthlyCashflowResult?.communication_cost?.toString() ?? "",
      misc_cost: monthlyCashflowResult?.misc_cost?.toString() ?? "",
      property_tax: monthlyCashflowResult?.property_tax?.toString() ?? "",
      supplies_cost: monthlyCashflowResult?.supplies_cost?.toString() ?? "",
      large_scale_repair_fund_cost: monthlyCashflowResult?.large_scale_repair_fund_cost?.toString() ?? "",
      misc_onetime_cost: monthlyCashflowResult?.misc_onetime_cost?.toString() ?? "",
    },
  });
  const { editMonthlyCashflowResult } = useEditMonthlyCashflowResult();

  const onSubmit = async (data: ManagementCostHistoryFormData) => {
    const originalMonth = monthlyCashflowResult?.month;
    const newMonth = `${data.month}-01`;

    const costTypeKey = getManagementCostTypeKey(costType);
    const fieldsToDelete = [costTypeKey];
    const hasValue = (value?: string) => value !== undefined && value !== null && value !== "";

    const sendData = {
      ...data,
      month: newMonth,
      ...(hasValue(data.building_management_cost) && {
        building_management_cost: parseFormattedNumber(data.building_management_cost),
      }),
      ...(hasValue(data.management_cost) && {
        management_cost: parseFormattedNumber(data.management_cost),
      }),
      ...(hasValue(data.repair_fund_cost) && {
        repair_fund_cost: parseFormattedNumber(data.repair_fund_cost),
      }),
      ...(hasValue(data.communication_cost) && {
        communication_cost: parseFormattedNumber(data.communication_cost),
      }),
      ...(hasValue(data.misc_cost) && {
        misc_cost: parseFormattedNumber(data.misc_cost),
      }),
      ...(hasValue(data.property_tax) && {
        property_tax: parseFormattedNumber(data.property_tax),
      }),
      ...(hasValue(data.supplies_cost) && {
        supplies_cost: parseFormattedNumber(data.supplies_cost),
      }),
      ...(hasValue(data.large_scale_repair_fund_cost) && {
        large_scale_repair_fund_cost: parseFormattedNumber(data.large_scale_repair_fund_cost),
      }),
      ...(hasValue(data.misc_onetime_cost) && {
        misc_onetime_cost: parseFormattedNumber(data.misc_onetime_cost),
      }),
    };

    await editMonthlyCashflowResult(fieldsToDelete, sendData, originalMonth);

    if (onSave) {
      onSave();
    } else if (onCreate) {
      onCreate();
    }
  };

  return (
    <Box className={classes.background} data-edit-form-panel>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box mb={2} data-edit-cost-inputs>
          <Controller
            name="month"
            control={control}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="いつから"
                required
                type="month"
                error={!!errors.month}
                errorText={errors.month?.message}
              />
            )}
          />
          {costType === "賃貸管理費" && (
            <Controller
              name="management_cost"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label={monthlyCashflowResult ? "金額" : "新しい金額"}
                  required
                  error={!!errors.management_cost}
                  errorText={errors.management_cost?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
          )}
          {costType === "建物管理費" && (
            <Controller
              name="building_management_cost"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label={monthlyCashflowResult ? "金額" : "新しい金額"}
                  required
                  error={!!errors.building_management_cost}
                  errorText={errors.building_management_cost?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
          )}
          {costType === "修繕積立金" && (
            <Controller
              name="repair_fund_cost"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label={monthlyCashflowResult ? "金額" : "新しい金額"}
                  required
                  error={!!errors.repair_fund_cost}
                  errorText={errors.repair_fund_cost?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
          )}
          {costType === "通信費" && (
            <Controller
              name="communication_cost"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label={monthlyCashflowResult ? "金額" : "新しい金額"}
                  required
                  error={!!errors.communication_cost}
                  errorText={errors.communication_cost?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
          )}
          {(costType === "その他経費" || costType === "その他の経費") && (
            <Controller
              name="misc_cost"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label={monthlyCashflowResult ? "金額" : "新しい金額"}
                  required
                  error={!!errors.misc_cost}
                  errorText={errors.misc_cost?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
          )}
          {costType === "固定資産税・都市計画税" && (
            <Controller
              name="property_tax"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label={monthlyCashflowResult ? "金額" : "新しい金額"}
                  required
                  error={!!errors.property_tax}
                  errorText={errors.property_tax?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
          )}
          {costType === "消耗品費" && (
            <Controller
              name="supplies_cost"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label={monthlyCashflowResult ? "金額" : "新しい金額"}
                  required
                  error={!!errors.supplies_cost}
                  errorText={errors.supplies_cost?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
          )}
          {costType === "設備修繕、大規模修繕費用" && (
            <Controller
              name="large_scale_repair_fund_cost"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label={monthlyCashflowResult ? "金額" : "新しい金額"}
                  required
                  error={!!errors.large_scale_repair_fund_cost}
                  errorText={errors.large_scale_repair_fund_cost?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
          )}
          {costType === "その他一過性の支出" && (
            <Controller
              name="misc_onetime_cost"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label={monthlyCashflowResult ? "金額" : "新しい金額"}
                  required
                  error={!!errors.misc_onetime_cost}
                  errorText={errors.misc_onetime_cost?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
          )}
        </Box>

        <CustomFormLabel
          label="適用月"
          helperText="実際に金額が変わった月を選んでください。その月から新しい金額が適用されます。"
        />
        <PropertyEditErrorSummary errors={errors} />
        <Grid container spacing={2} data-edit-form-actions>
          <Grid item xs={6}>
            <CustomButton customVariant="textPrimary" onClick={onCancel} size="small" fullWidth>
              キャンセル
            </CustomButton>
          </Grid>
          <Grid item xs={6}>
            <CustomButton buttonSize="L" customVariant="filled" type="submit" size="small" fullWidth>
              {monthlyCashflowResult ? "保存する" : "この内容で記録する"}
            </CustomButton>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
